import { ErrorText } from "./ErrorText";
import { LoadingIndicator } from "./LoadingIndicator";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { cn } from "@/lib/utils";

export const ConfirmationDialog = ({
  cancelButtonText,
  children,
  confirmButtonText,
  confirmationButtonVariant,
  description,
  errorMessage,
  isError,
  isLoading,
  onConfirm,
  onOpenChange,
  open,
  preventContentClickPropagation,
  preventOverlayClickCapturePropagation,
  title,
  variant = "default",
}: {
  cancelButtonText?: string;
  // Optional extra content (e.g. an opt-in checkbox) rendered between the
  // description and the footer buttons.
  children?: React.ReactNode;
  confirmButtonText?: string;
  confirmationButtonVariant?: "default" | "destructive";
  description: string;
  errorMessage?: string;
  isError?: boolean;
  isLoading?: boolean;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  preventContentClickPropagation?: boolean;
  preventOverlayClickCapturePropagation?: boolean;
  title: string;
  // "square" is the compact squared-corner style; cmd+enter always confirms.
  variant?: "default" | "square";
}) => {
  const isSquare = variant === "square";

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent
        className={cn(
          "flex flex-col focus:outline-none",
          isSquare ? "max-w-md gap-4" : "gap-10",
        )}
        onKeyDown={(e) => {
          if (e.key !== "Enter" || e.repeat || isLoading) return;

          // Cmd/ctrl+enter always confirms on the square variant, regardless
          // of which element is focused.
          if (isSquare && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            onConfirm();
            return;
          }

          // Ignore Enter originating from interactive elements (buttons,
          // the checkbox rendered via `children`, inputs, ...) — they handle
          // their own activation. This also covers the cancel button, and
          // prevents a double-fire when the confirm button itself is focused
          // (native click + this handler).
          const isInteractiveTarget = (e.target as HTMLElement).closest(
            'button, a, input, textarea, select, [role="checkbox"], [role="switch"]',
          );
          if (!isInteractiveTarget) {
            e.preventDefault();
            onConfirm();
          }
        }}
        onClick={
          preventContentClickPropagation
            ? (e) => {
                e.stopPropagation();
              }
            : undefined
        }
        // Radix auto-focuses the first focusable element on open (the Cancel
        // button, or the checkbox rendered via `children`), which swallows
        // Enter-to-confirm. Focus the dialog itself instead — preventDefault
        // alone leaves focus OUTSIDE the dialog, where our onKeyDown never
        // hears the Enter. Tab still reaches every control.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          (e.target as HTMLElement | null)?.focus();
        }}
        preventOverlayClickCapturePropagation={
          preventOverlayClickCapturePropagation
        }
      >
        <DialogHeader className={cn(!isSquare && "flex flex-col gap-4")}>
          <DialogTitle
            className={cn(!isSquare && "text-2xl font-semibold text-slate-700")}
          >
            {title}
          </DialogTitle>
          <DialogDescription
            className={cn(
              "whitespace-pre-line",
              !isSquare && "!m-0 text-base font-medium text-slate-700",
            )}
          >
            {description}
          </DialogDescription>
        </DialogHeader>
        {children}
        <DialogFooter>
          <Button
            className={cn(!isSquare && "rounded-full")}
            data-confirmation-cancel="true"
            disabled={isLoading}
            onClick={() => {
              onOpenChange(false);
            }}
            variant={isSquare ? "outline" : "secondary"}
          >
            {cancelButtonText ?? "Cancel"}
          </Button>
          <Button
            className={cn(!isSquare && "min-w-24 rounded-full")}
            disabled={isLoading}
            onClick={onConfirm}
            variant={confirmationButtonVariant}
          >
            {isLoading ? (
              <LoadingIndicator gearColor="white" variant="small" />
            ) : (
              <span>{confirmButtonText ?? "Confirm"}</span>
            )}
          </Button>
        </DialogFooter>
        {isError && errorMessage && <ErrorText errorMessage={errorMessage} />}
      </DialogContent>
    </Dialog>
  );
};
